import { lazy } from "react";
import { trpc } from "@/lib/trpc";
import { porcentagem, reais } from "@/lib/dinheiro";
import { Indicadores } from "./Indicadores";
import { Alertas } from "./Alertas";
import { GraficoOuTabela } from "./GraficoOuTabela";

const graficos = () => import("./graficos");
const GraficoResultado = lazy(() => graficos().then((m) => ({ default: m.GraficoResultado })));
const GraficoFluxo = lazy(() => graficos().then((m) => ({ default: m.GraficoFluxo })));
const GraficoRosca = lazy(() => graficos().then((m) => ({ default: m.GraficoRosca })));
const GraficoBarrasDeitadas = lazy(() => graficos().then((m) => ({ default: m.GraficoBarrasDeitadas })));
const GraficoServicos = lazy(() => graficos().then((m) => ({ default: m.GraficoServicos })));

const MESES = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
const mesCurto = (c: string) => `${MESES[Number(c.slice(5, 7)) - 1]}/${c.slice(2, 4)}`;
const diaCurto = (d: string) => `${d.slice(8, 10)}/${d.slice(5, 7)}`;
const IDADES = [
  { chave: "NO_MES", nome: "Do mês", cor: "#2457FF" },
  { chave: "ATE_30", nome: "Até 30 dias", cor: "#f59e0b" },
  { chave: "DE_31_A_60", nome: "31 a 60 dias", cor: "#f97316" },
  { chave: "MAIS_DE_60", nome: "Mais de 60 dias", cor: "#ef4444" },
] as const;

export function VisaoDoMes({ competencia, onIr }: { competencia: string; onIr: (aba: string) => void }) {
  const serie = trpc.financeiro.visao.serie.useQuery({ competencia }).data;
  const mes = trpc.financeiro.visao.mes.useQuery({ competencia }).data;
  if (!serie) return <div className="h-64 animate-pulse rounded-xl bg-zinc-100 dark:bg-muted" />;

  const pontos = serie.map((s) => ({
    mes: mesCurto(s.competencia),
    receita: s.receitaCents,
    custoVariavel: s.custoVariavelCents,
    fixos: s.custosFixosCents,
    resultado: s.resultadoCents,
  }));
  const fluxo = (mes?.fluxo ?? []).map((f) => ({
    dia: diaCurto(f.dia),
    entradas: f.entradasCents,
    saidas: f.saidasCents,
    saldo: f.saldoCents,
    previstoEntradas: f.previstoEntradasCents,
    previstoSaidas: f.previstoSaidasCents,
  }));
  const comMovimento = fluxo.some((f) => f.entradas || f.saidas || f.previstoEntradas || f.previstoSaidas);
  const clientes = (mes?.porCliente ?? []).slice(0, 10);
  const totalClientes = (mes?.porCliente ?? []).reduce((t, c) => t + c.valorCents, 0);
  const concentracao = clientes[0] && totalClientes ? clientes[0].valorCents / totalClientes : null;
  const idade = IDADES.map((i) => ({ nome: i.nome, cor: i.cor, valorCents: mes?.idade[i.chave] ?? 0 })).filter((i) => i.valorCents > 0);

  return (
    <div className="space-y-4">
      <Indicadores serie={serie} mes={mes} />
      {mes && <Alertas alertas={mes.alertas} onIr={onIr} />}

      <div className="grid gap-4 lg:grid-cols-2">
        <GraficoOuTabela
          className="lg:col-span-2"
          titulo="Resultado dos últimos 12 meses"
          descricao="Receita, custos e o que sobrou, mês a mês (regime de competência)"
          nomeDoArquivo={`resultado-12-meses-${competencia}`}
          linhas={serie}
          colunas={[
            { titulo: "Mês", valor: (s) => mesCurto(s.competencia) },
            { titulo: "Receita", valor: (s) => s.receitaCents / 100, texto: (s) => reais(s.receitaCents), numero: true },
            { titulo: "Custo variável", valor: (s) => s.custoVariavelCents / 100, texto: (s) => reais(s.custoVariavelCents), numero: true },
            { titulo: "Margem", valor: (s) => porcentagem(s.margemPct), numero: true },
            { titulo: "Custos fixos", valor: (s) => s.custosFixosCents / 100, texto: (s) => reais(s.custosFixosCents), numero: true },
            { titulo: "Resultado", valor: (s) => s.resultadoCents / 100, texto: (s) => reais(s.resultadoCents), numero: true },
            { titulo: "Recebido", valor: (s) => s.recebidoCents / 100, texto: (s) => reais(s.recebidoCents), numero: true },
            { titulo: "Pago", valor: (s) => s.pagoCents / 100, texto: (s) => reais(s.pagoCents), numero: true },
          ]}
        >
          <GraficoResultado dados={pontos} />
        </GraficoOuTabela>

        <GraficoOuTabela
          className="lg:col-span-2"
          titulo="Fluxo de caixa do mês"
          descricao="O que entrou e saiu por dia, o saldo acumulado e o que ainda está previsto"
          nomeDoArquivo={`fluxo-de-caixa-${competencia}`}
          linhas={comMovimento ? fluxo : []}
          vazio="Nenhum pagamento ou recebimento neste mês."
          colunas={[
            { titulo: "Dia", valor: (f) => f.dia },
            { titulo: "Entradas", valor: (f) => f.entradas / 100, texto: (f) => reais(f.entradas), numero: true },
            { titulo: "Saídas", valor: (f) => f.saidas / 100, texto: (f) => reais(f.saidas), numero: true },
            { titulo: "Saldo", valor: (f) => (f.saldo == null ? null : f.saldo / 100), texto: (f) => (f.saldo == null ? "—" : reais(f.saldo)), numero: true },
            { titulo: "A entrar", valor: (f) => f.previstoEntradas / 100, texto: (f) => reais(f.previstoEntradas), numero: true },
            { titulo: "A sair", valor: (f) => f.previstoSaidas / 100, texto: (f) => reais(f.previstoSaidas), numero: true },
          ]}
        >
          <GraficoFluxo dados={fluxo} />
        </GraficoOuTabela>

        <GraficoOuTabela
          titulo="Despesas por categoria"
          descricao="Inclui o material consumido na produção"
          nomeDoArquivo={`despesas-${competencia}`}
          linhas={mes?.porCategoria ?? []}
          colunas={[
            { titulo: "Categoria", valor: (c) => c.nome },
            { titulo: "Valor", valor: (c) => c.valorCents / 100, texto: (c) => reais(c.valorCents), numero: true },
          ]}
        >
          <GraficoRosca dados={mes?.porCategoria ?? []} />
        </GraficoOuTabela>

        <GraficoOuTabela
          titulo="Faturamento por cliente"
          descricao={concentracao != null ? `O maior cliente responde por ${porcentagem(concentracao)} do faturamento` : undefined}
          nomeDoArquivo={`faturamento-por-cliente-${competencia}`}
          linhas={clientes}
          colunas={[
            { titulo: "Cliente", valor: (c) => c.nome },
            { titulo: "Faturado", valor: (c) => c.valorCents / 100, texto: (c) => reais(c.valorCents), numero: true },
          ]}
        >
          <GraficoBarrasDeitadas dados={clientes} />
        </GraficoOuTabela>

        <GraficoOuTabela
          titulo="Margem por serviço"
          descricao="Receita e custo do material de cada serviço concluído"
          nomeDoArquivo={`margem-por-servico-${competencia}`}
          linhas={mes?.porServico ?? []}
          colunas={[
            { titulo: "Serviço", valor: (s) => s.nome },
            { titulo: "Qtd.", valor: (s) => s.quantidade, numero: true },
            { titulo: "Receita", valor: (s) => s.receitaCents / 100, texto: (s) => reais(s.receitaCents), numero: true },
            { titulo: "Custo", valor: (s) => s.custoCents / 100, texto: (s) => reais(s.custoCents), numero: true },
            { titulo: "Margem", valor: (s) => s.margemCents / 100, texto: (s) => reais(s.margemCents), numero: true },
          ]}
        >
          <GraficoServicos dados={(mes?.porServico ?? []).slice(0, 8)} />
        </GraficoOuTabela>

        <GraficoOuTabela
          titulo="A receber por idade"
          descricao="Saldo dos extratos em aberto, pelo tempo desde o fim do mês"
          nomeDoArquivo={`a-receber-por-idade-${competencia}`}
          linhas={idade}
          vazio="Nada em aberto."
          colunas={[
            { titulo: "Idade", valor: (i) => i.nome },
            { titulo: "Saldo", valor: (i) => i.valorCents / 100, texto: (i) => reais(i.valorCents), numero: true },
          ]}
        >
          <GraficoRosca dados={idade} />
        </GraficoOuTabela>
      </div>
    </div>
  );
}
